import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  linkPurchaseOrder,
  unlinkPurchaseOrder,
  getJournalPreview,
  getPaymentRequest,
} from "redux/erp/paymentRequestSlice";
import Clickable from "../common/Clickable";
import PurchaseOrderDrawer from "./PurchaseOrderDrawer";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");

const MockTag = () => (
  <span
    title="Running the mock adapter — not connected to the real ERP"
    className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-bold tw-uppercase tw-bg-[#fff8ee] tw-text-[#a05e03] tw-border tw-border-solid tw-border-[#f7e2c0]"
  >
    <i className="bi bi-cone-striped" /> Mock
  </span>
);

const Row = ({ label, value }) => (
  <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-sm">
    <span className="tw-text-geist-fg-2">{label}</span>
    <span className="tw-flex-1 tw-border-0 tw-border-b tw-border-dotted tw-border-geist-border" />
    <span className="tw-font-semibold tw-text-geist-fg tw-tabular-nums">{value || "—"}</span>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * Purchase Order — căn cứ để dựng Journal.
 *
 * Line Manager duyệt xong, Kế toán phải link 1 PO (bên AX) rồi mới tạo được Journal.
 * Card tự ẩn khi phiếu chưa tới bước này và không có gì để hiện.
 *
 * Không tự gọi toast.error: addRejectedMatcher trong slice đã toast cho mọi
 * PaymentRequest/* bị reject rồi, thêm nữa là double-toast.
 */
const PurchaseOrderCard = ({ paymentRequest: pr }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const po = pr?.purchase_order || {};
  const perms = pr?.permissions || {};
  const journal = pr?.journal;
  const blockers = pr?.blocked_by || [];

  // PO chỉ thuộc luồng 2 (po_record). Luồng 1/3 là phiếu thường: bút toán dựng từ
  // Invoice details ở tab "Journal", card này không có gì để nói.
  if (pr?.workflow_type !== "po_record" && !po.po_num) return null;
  if (!po.po_num && !perms.allow_link_po && !journal) return null;

  const snap = po.snapshot || {};

  const run = async (thunk) => {
    setBusy(true);
    try {
      await dispatch(thunk).unwrap();
      await dispatch(getPaymentRequest(pr.id));
      return true;
    } catch {
      return false; // slice đã toast
    } finally {
      setBusy(false);
    }
  };

  const doLink = async (poNum) => {
    const ok = await run(linkPurchaseOrder({ id: pr.id, poNum }));
    if (ok) {
      setPickerOpen(false);
      setPreview(null);
      toast.success(`Purchase order ${poNum} linked.`);
    }
  };

  const doUnlink = async () => {
    const ok = await run(unlinkPurchaseOrder(pr.id));
    if (ok) {
      setPreview(null);
      toast.success("Purchase order unlinked.");
    }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      setPreview(await dispatch(getJournalPreview(pr.id)).unwrap());
    } catch {
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
      <div className="tw-px-5 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-flex tw-items-center tw-gap-2">
        <i className="bi bi-receipt-cutoff tw-text-geist-fg-3" />
        <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">Purchase order</span>
        {!po.live && <MockTag />}
      </div>

      <div className="tw-p-5 tw-space-y-5">
        {/* Vì sao bước này chưa ký được — nói lý do, không chỉ ẩn nút. */}
        {blockers.length > 0 && (
          <div className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-3 tw-py-2">
            {blockers.map((b) => (
              <div key={b.code} className="tw-text-[12px] tw-text-[#a05e03]">
                {b.message}
              </div>
            ))}
          </div>
        )}

        {!po.po_num ? (
          <p className="tw-text-[13px] tw-text-geist-fg-3 tw-m-0">
            No purchase order linked yet. The journal is built from the PO — vendor, expense account,
            tax and the three dimensions all come from it.
          </p>
        ) : (
          <>
            <div className="tw-space-y-1.5">
              <Row label="PO number" value={po.po_num} />
              <Row label="Vendor" value={snap.vendorName} />
              <Row
                label="Amount"
                value={snap.totalAmount ? `${money(snap.totalAmount)} ${snap.currency || ""}` : null}
              />
              <Row label="Invoice" value={snap.invoiceRef} />
              <Row
                label="Dimensions"
                value={[snap.dimDepartment, snap.dimCostCenter, snap.dimPurpose].filter(Boolean).join(" / ")}
              />
            </div>
            {po.linked_by && (
              <div className="tw-text-[12px] tw-text-geist-fg-3">
                Linked by {po.linked_by}
                {po.linked_at ? ` · ${po.linked_at}` : ""}
              </div>
            )}
          </>
        )}

        {/* Bút toán — xem trước, hoặc đã tạo thật */}
        {(preview || journal) && (
          <div className="tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-wrap">
              <span className="tw-text-[12px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-3">
                {journal ? "Journal" : "Journal preview"}
              </span>
              <span className="tw-text-[12px] tw-text-geist-fg-3">
                {journal
                  ? `${journal.journal_num} · ${journal.journal_type}`
                  : preview?.data?.journal_type}
              </span>
              <span className="tw-flex-1" />
              <span
                className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-bold ${
                  (journal ? journal.balanced : preview?.balanced)
                    ? "tw-bg-[#edfaef] tw-text-geist-success"
                    : "tw-bg-[#fff0f0] tw-text-geist-error"
                }`}
              >
                {(journal ? journal.balanced : preview?.balanced) ? "Balanced" : "Not balanced"}
              </span>
            </div>

            <div className="table-scroll tw-overflow-x-auto">
              <table className="tw-w-full tw-text-[12px]">
                <thead>
                  <tr className="tw-text-geist-fg-3 tw-text-left">
                    <th className="tw-py-1 tw-pr-2 tw-font-semibold">Type</th>
                    <th className="tw-py-1 tw-pr-2 tw-font-semibold">Account</th>
                    <th className="tw-py-1 tw-pr-2 tw-font-semibold tw-text-right">Debit</th>
                    <th className="tw-py-1 tw-pr-2 tw-font-semibold tw-text-right">Credit</th>
                    <th className="tw-py-1 tw-font-semibold">Dimensions</th>
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-geist-border">
                  {(journal?.lines || preview?.data?.lines || []).map((l) => (
                    <tr key={l.line_num}>
                      <td className="tw-py-1.5 tw-pr-2 tw-text-geist-fg-2">{l.account_type}</td>
                      <td className="tw-py-1.5 tw-pr-2 tw-text-geist-fg tw-tabular-nums">{l.account}</td>
                      <td className="tw-py-1.5 tw-pr-2 tw-text-right tw-tabular-nums">
                        {Number(l.debit) ? money(l.debit) : ""}
                      </td>
                      <td className="tw-py-1.5 tw-pr-2 tw-text-right tw-tabular-nums">
                        {Number(l.credit) ? money(l.credit) : ""}
                      </td>
                      <td className="tw-py-1.5 tw-text-geist-fg-3">
                        {[l.dim_department, l.dim_cost_center, l.dim_purpose].filter(Boolean).join("/")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!journal && (
              <p className="tw-text-[12px] tw-text-geist-fg-3 tw-m-0 tw-mt-2">
                Preview only — nothing has been sent to ERP yet.
              </p>
            )}
          </div>
        )}

        {/* Thao tác */}
        {(perms.allow_link_po || perms.allow_unlink_po) && (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pt-4 tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
            {perms.allow_link_po && (
              <Clickable
                onClick={() => setPickerOpen(true)}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-accent hover:tw-bg-geist-100"
              >
                <i className="bi bi-link-45deg" /> {po.po_num ? "Change purchase order" : "Link purchase order"}
              </Clickable>
            )}

            {po.po_num && !journal && (
              <Clickable
                onClick={doPreview}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-fg-2 hover:tw-bg-geist-100"
              >
                <i className="bi bi-eye" /> Preview journal
              </Clickable>
            )}

            {perms.allow_unlink_po && (
              <Clickable
                onClick={doUnlink}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-[13px] tw-font-semibold tw-text-geist-fg-2 hover:tw-bg-geist-100"
              >
                <i className="bi bi-x-lg" /> Unlink
              </Clickable>
            )}
          </div>
        )}
      </div>

      <PurchaseOrderDrawer
        show={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={doLink}
        busy={busy}
      />
    </div>
  );
};

PurchaseOrderCard.propTypes = {
  paymentRequest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    workflow_type: PropTypes.string,
    purchase_order: PropTypes.shape({
      po_num: PropTypes.string,
      live: PropTypes.bool,
      linked_by: PropTypes.string,
      linked_at: PropTypes.string,
      snapshot: PropTypes.object,
    }),
    journal: PropTypes.shape({
      journal_num: PropTypes.string,
      journal_type: PropTypes.string,
      balanced: PropTypes.bool,
      lines: PropTypes.array,
    }),
    blocked_by: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      message: PropTypes.string,
    })),
    permissions: PropTypes.shape({
      allow_link_po: PropTypes.bool,
      allow_unlink_po: PropTypes.bool,
    }),
  }),
};

export default PurchaseOrderCard;
